import {
  captureChatScrollPosition,
  readChatScrollPosition,
  removeChatScrollPosition,
} from "@/hooks/ai-chat/chat-scroll-position";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type RefObject,
  type UIEvent,
} from "react";

const FOLLOW_DISTANCE_PX = 96;
const SAVE_DEBOUNCE_MS = 120;
const FIND_SCROLL_TIMEOUT_MS = 1_200;

type ScrollToRow = (
  index: number,
  options: { align: "end" | "start"; behavior: "auto" },
) => void;

type UseChatTimelineViewportInput = {
  contentHeight: number;
  findRowIndex: (itemId: string) => number;
  hasNewer: boolean;
  isLoadingInitial: boolean;
  latestSignal: string;
  onAtBottomChange?: (atBottom: boolean) => void;
  onRevealItem: (itemId: string) => Promise<void>;
  revision: number;
  rowCount: number;
  scrollRef: RefObject<HTMLDivElement>;
  scrollToRow: ScrollToRow;
  threadId: string;
};

function renderedAnchorElement(container: HTMLDivElement, itemId: string) {
  return [
    ...container.querySelectorAll<HTMLElement>("[data-timeline-item-ids]"),
  ].find((candidate) =>
    candidate.dataset.timelineItemIds?.split(" ").includes(itemId),
  );
}

export function useChatTimelineViewport({
  contentHeight,
  findRowIndex,
  hasNewer,
  isLoadingInitial,
  latestSignal,
  onAtBottomChange,
  onRevealItem,
  revision,
  rowCount,
  scrollRef,
  scrollToRow,
  threadId,
}: UseChatTimelineViewportInput) {
  const savedPositionRef = useRef(readChatScrollPosition(threadId));
  const activeRef = useRef(true);
  const restoredRef = useRef(false);
  const revealScheduledRef = useRef(false);
  const revealPendingRef = useRef(false);
  const revealFinishedRef = useRef(false);
  const programmaticScrollRef = useRef(false);
  const wasFollowingRef = useRef(false);
  const settleFrameRef = useRef<number>();
  const followFrameRef = useRef<number>();
  const lastFollowTopRef = useRef<number>();
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const findTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const latestRevisionRef = useRef(revision);
  const latestSignalRef = useRef(latestSignal);
  const initialAtBottomChangeRef = useRef(onAtBottomChange);
  const [restoreVersion, setRestoreVersion] = useState(0);
  const [isViewportReady, setIsViewportReady] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [hasNewActivity, setHasNewActivity] = useState(false);
  latestRevisionRef.current = revision;
  const latestViewportRef = useRef({ hasNewer, rowCount, scrollToRow });
  latestViewportRef.current = { hasNewer, rowCount, scrollToRow };

  useLayoutEffect(() => {
    activeRef.current = true;
    initialAtBottomChangeRef.current?.(false);
    return () => {
      activeRef.current = false;
    };
  }, []);

  const setFollowing = useCallback(
    (following: boolean) => {
      if (wasFollowingRef.current === following) return;
      wasFollowingRef.current = following;
      setIsFollowing(following);
      if (following) setHasNewActivity(false);
      onAtBottomChange?.(following);
    },
    [onAtBottomChange],
  );

  const scheduleFollow = useCallback(() => {
    if (
      followFrameRef.current !== undefined ||
      document.visibilityState === "hidden"
    )
      return;
    followFrameRef.current = requestAnimationFrame(() => {
      followFrameRef.current = undefined;
      const container = scrollRef.current;
      if (
        !container ||
        !restoredRef.current ||
        !wasFollowingRef.current ||
        latestViewportRef.current.hasNewer ||
        document.visibilityState === "hidden"
      )
        return;
      const top = Math.max(0, container.scrollHeight - container.clientHeight);
      if (Math.abs(container.scrollTop - top) > 0.5) {
        container.scrollTop = top;
        lastFollowTopRef.current = container.scrollTop;
      }
    });
  }, [scrollRef]);

  const finishRestoration = useCallback(
    (following: boolean, newActivity = false) => {
      restoredRef.current = true;
      programmaticScrollRef.current = false;
      latestSignalRef.current = latestSignal;
      setIsViewportReady(true);
      setHasNewActivity(newActivity);
      setFollowing(following);
    },
    [latestSignal, setFollowing],
  );

  const scrollToLatest = useCallback(() => {
    const container = scrollRef.current;
    if (!container || latestViewportRef.current.rowCount === 0) return;
    programmaticScrollRef.current = true;
    if (settleFrameRef.current !== undefined) {
      cancelAnimationFrame(settleFrameRef.current);
    }
    latestViewportRef.current.scrollToRow(
      latestViewportRef.current.rowCount - 1,
      { align: "end", behavior: "auto" },
    );
    settleFrameRef.current = requestAnimationFrame(() => {
      latestViewportRef.current.scrollToRow(
        latestViewportRef.current.rowCount - 1,
        { align: "end", behavior: "auto" },
      );
      settleFrameRef.current = requestAnimationFrame(() => {
        const currentContainer = scrollRef.current;
        if (!currentContainer) return;
        currentContainer.scrollTop = currentContainer.scrollHeight;
        lastFollowTopRef.current = currentContainer.scrollTop;
        programmaticScrollRef.current = false;
        if (restoredRef.current)
          setFollowing(!latestViewportRef.current.hasNewer);
        else finishRestoration(!latestViewportRef.current.hasNewer);
      });
    });
  }, [finishRestoration, scrollRef, setFollowing]);

  useLayoutEffect(() => {
    if (restoredRef.current || isLoadingInitial) return;
    if (rowCount === 0) {
      finishRestoration(true);
      return;
    }

    const savedPosition = savedPositionRef.current;
    if (!savedPosition) {
      scrollToLatest();
      return;
    }

    if (!revealFinishedRef.current) {
      if (!revealScheduledRef.current && !revealPendingRef.current) {
        revealScheduledRef.current = true;
        queueMicrotask(() => {
          revealScheduledRef.current = false;
          if (!activeRef.current) return;
          revealPendingRef.current = true;
          void onRevealItem(savedPosition.itemId)
            .catch(() => {
              if (!activeRef.current) return;
              savedPositionRef.current = null;
              removeChatScrollPosition(threadId);
            })
            .finally(() => {
              if (!activeRef.current) return;
              revealPendingRef.current = false;
              revealFinishedRef.current = true;
              setRestoreVersion((value) => value + 1);
            });
        });
      }
      return;
    }

    const refreshedPosition = savedPositionRef.current;
    if (!refreshedPosition) {
      scrollToLatest();
      return;
    }
    const rowIndex = findRowIndex(refreshedPosition.itemId);
    if (rowIndex < 0) {
      removeChatScrollPosition(threadId);
      savedPositionRef.current = null;
      scrollToLatest();
      return;
    }

    if (
      refreshedPosition.atBottom &&
      refreshedPosition.revision === revision &&
      !hasNewer
    ) {
      scrollToLatest();
      return;
    }

    programmaticScrollRef.current = true;
    scrollToRow(rowIndex, { align: "start", behavior: "auto" });
    let attempts = 0;
    const refinePosition = () => {
      const container = scrollRef.current;
      const anchor = container
        ? renderedAnchorElement(container, refreshedPosition.itemId)
        : undefined;
      if (!container || !anchor) {
        attempts += 1;
        if (attempts < 30) {
          settleFrameRef.current = requestAnimationFrame(refinePosition);
        } else {
          removeChatScrollPosition(threadId);
          finishRestoration(false, hasNewer);
        }
        return;
      }
      const currentOffset =
        anchor.getBoundingClientRect().top -
        container.getBoundingClientRect().top;
      container.scrollTop += currentOffset - refreshedPosition.viewportOffset;
      finishRestoration(
        false,
        hasNewer || revision > refreshedPosition.revision,
      );
    };
    settleFrameRef.current = requestAnimationFrame(refinePosition);
  }, [
    findRowIndex,
    finishRestoration,
    hasNewer,
    isLoadingInitial,
    onRevealItem,
    restoreVersion,
    revision,
    rowCount,
    scrollRef,
    scrollToLatest,
    scrollToRow,
    threadId,
  ]);

  useEffect(() => {
    if (!isViewportReady || latestSignalRef.current === latestSignal) return;
    latestSignalRef.current = latestSignal;
    if (wasFollowingRef.current) {
      scheduleFollow();
    } else {
      setHasNewActivity(true);
    }
  }, [isViewportReady, latestSignal, scheduleFollow]);

  const savePosition = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !restoredRef.current) return;
    captureChatScrollPosition({
      container,
      revision: latestRevisionRef.current,
      threadId,
    });
  }, [scrollRef, threadId]);

  // Streaming text grows between server revisions. Keep following its measured
  // height, but leave readers and expanded tool groups at their chosen position.
  useLayoutEffect(() => {
    if (restoredRef.current && wasFollowingRef.current && !hasNewer)
      scheduleFollow();
  }, [contentHeight, hasNewer, scheduleFollow]);

  useEffect(() => {
    const visibility = () => {
      if (document.visibilityState === "hidden") {
        if (followFrameRef.current !== undefined)
          cancelAnimationFrame(followFrameRef.current);
        followFrameRef.current = undefined;
      } else scheduleFollow();
    };
    document.addEventListener("visibilitychange", visibility);
    return () => {
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [scheduleFollow]);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (!restoredRef.current || programmaticScrollRef.current)
        return wasFollowingRef.current;
      const container = event.currentTarget;
      // A native scroll event can arrive after another height change has
      // queued our next follow frame. It is not a gesture away from the tail.
      if (
        wasFollowingRef.current &&
        followFrameRef.current !== undefined &&
        container.scrollTop === lastFollowTopRef.current
      )
        return true;
      const atBottom =
        !hasNewer &&
        container.scrollHeight - container.scrollTop - container.clientHeight <=
          (wasFollowingRef.current ? FOLLOW_DISTANCE_PX : 1);
      setFollowing(atBottom);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(savePosition, SAVE_DEBOUNCE_MS);
      return atBottom;
    },
    [hasNewer, savePosition, setFollowing],
  );

  const beginFindNavigation = useCallback(() => {
    const container = scrollRef.current;
    if (followFrameRef.current !== undefined) {
      cancelAnimationFrame(followFrameRef.current);
      followFrameRef.current = undefined;
    }
    if (settleFrameRef.current !== undefined) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = undefined;
    }
    programmaticScrollRef.current = true;
    setFollowing(false);
    const finish = () => {
      programmaticScrollRef.current = false;
      if (findTimerRef.current) clearTimeout(findTimerRef.current);
      container?.removeEventListener("scrollend", finish);
    };
    container?.addEventListener("scrollend", finish, { once: true });
    if (findTimerRef.current) clearTimeout(findTimerRef.current);
    findTimerRef.current = setTimeout(finish, FIND_SCROLL_TIMEOUT_MS);
  }, [scrollRef, setFollowing]);

  const stopFollowing = useCallback(() => {
    if (followFrameRef.current !== undefined) {
      cancelAnimationFrame(followFrameRef.current);
      followFrameRef.current = undefined;
    }
    if (settleFrameRef.current !== undefined) {
      cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = undefined;
      programmaticScrollRef.current = false;
    }
    setFollowing(false);
  }, [setFollowing]);

  // A new chat gains its server ID without unmounting. Keep scheduled viewport
  // work alive during that promotion, and save under its current ID on unmount.
  const savePositionRef = useRef(savePosition);
  savePositionRef.current = savePosition;
  useLayoutEffect(
    () => () => {
      savePositionRef.current();
      if (settleFrameRef.current !== undefined) {
        cancelAnimationFrame(settleFrameRef.current);
      }
      if (followFrameRef.current !== undefined) {
        cancelAnimationFrame(followFrameRef.current);
      }
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (findTimerRef.current) clearTimeout(findTimerRef.current);
    },
    [],
  );

  return {
    beginFindNavigation,
    handleScroll,
    hasNewActivity,
    isFollowing,
    isViewportReady,
    scrollToLatest,
    stopFollowing,
  };
}
